/**
 * Module-level Flag, das den Support-Modus-Status global verfügbar macht.
 *
 * Hintergrund: Der `SupportModeContext` lebt im React-Tree, aber der
 * Supabase-Client-Wrapper (`client.ts`) ist ein gewöhnliches TS-Modul
 * ohne Zugriff auf React-Hooks. Damit der Wrapper trotzdem entscheiden
 * kann, ob ein Schreibversuch zugelassen wird, syncen wir den Context-
 * Status hierhin (`useEffect` im Provider ruft `setSupportModeActive`).
 *
 * Bewusst klein gehalten: zwei Funktionen, ein bool. Kein Event-Emitter,
 * kein Store. Wer den Status reaktiv braucht, nimmt den Hook
 * `useSupportMode()`; wer nur in einer Mutation prüfen will, fragt
 * `isSupportModeActive()` direkt.
 */

let supportModeActive = false;

export function setSupportModeActive(active: boolean): void {
  supportModeActive = active;
}

export function isSupportModeActive(): boolean {
  return supportModeActive;
}

/**
 * Wird vom Supabase-Client-Wrapper geworfen, wenn ein Insert/Update/
 * Delete/Upsert während eines Support-Modus versucht wird. Wir
 * subclassen Error, damit TanStack Query-Mutations das Fehlerobjekt
 * in `onError` per `instanceof`-Check erkennen können (z. B. um eine
 * freundliche Toast statt der rohen RLS-Fehlermeldung zu zeigen).
 */
export class SupportModeWriteBlockedError extends Error {
  readonly method: string;
  readonly table: string | null;

  constructor(method: string, table: string | null) {
    const tableSuffix = table ? ` auf ${table}` : '';
    super(
      `Im Support-Modus sind Änderungen blockiert (${method}${tableSuffix}). Beende den Support-Modus, um Daten in deiner eigenen Org zu bearbeiten.`,
    );
    this.name = 'SupportModeWriteBlockedError';
    this.method = method;
    this.table = table;
  }
}
